import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
    AlertTriangle, 
    AlertCircle, 
    CheckCircle,
    Stethoscope,
    Pill,
    Heart,
    Search,
    Clock,
    FileText,
    Lightbulb,
    Activity,
    User,
    Shield,
    Image as ImageIcon,
    MessageSquare,
    Send,
    UserCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const urgencyConfig = {
    EMERGENCY: { 
        icon: AlertTriangle, 
        class: 'bg-red-500',
        textClass: 'text-red-600',
        bgClass: 'bg-red-50 dark:bg-red-950/30',
        borderClass: 'border-red-200 dark:border-red-800',
        label: 'Emergency',
        description: 'Seek immediate medical attention'
    },
    URGENT: { 
        icon: AlertCircle, 
        class: 'bg-amber-500',
        textClass: 'text-amber-600',
        bgClass: 'bg-amber-50 dark:bg-amber-950/30',
        borderClass: 'border-amber-200 dark:border-amber-800',
        label: 'Urgent',
        description: 'See a doctor within 24-48 hours'
    },
    ROUTINE: { 
        icon: CheckCircle, 
        class: 'bg-emerald-500',
        textClass: 'text-emerald-600',
        bgClass: 'bg-emerald-50 dark:bg-emerald-950/30',
        borderClass: 'border-emerald-200 dark:border-emerald-800',
        label: 'Routine',
        description: 'Can wait for a scheduled appointment'
    }
};

export default function SharedDiagnosis() {
    const { token } = useParams();
    const [diagnosis, setDiagnosis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Doctor notes state
    const [doctorName, setDoctorName] = useState('');
    const [noteText, setNoteText] = useState('');
    const [submittingNote, setSubmittingNote] = useState(false);
    const [notes, setNotes] = useState([]);

    useEffect(() => {
        fetchSharedDiagnosis();
    }, [token]);

    const fetchSharedDiagnosis = async () => {
        try {
            const response = await axios.get(`${API}/shared/${token}`);
            setDiagnosis(response.data);
            setNotes(response.data.doctor_notes || []);
        } catch (err) {
            if (err.response?.status === 404) {
                setError('This share link has expired or is invalid.');
            } else {
                setError('Failed to load the shared assessment.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAddNote = async (e) => {
        e.preventDefault();
        if (!doctorName.trim() || !noteText.trim()) {
            toast.error('Please fill in both your name and note');
            return;
        }

        setSubmittingNote(true);
        try {
            const response = await axios.post(`${API}/shared/${token}/notes`, {
                doctor_name: doctorName,
                note: noteText
            });
            setNotes([...notes, response.data.note]);
            setNoteText('');
            toast.success('Note added successfully');
        } catch (err) {
            toast.error('Failed to add note');
        } finally {
            setSubmittingNote(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-center">
                    <div className="spinner mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading shared assessment...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background px-4">
                <Card className="max-w-md w-full">
                    <CardContent className="pt-6 text-center">
                        <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
                        <h2 className="text-xl font-semibold mb-2">Link Expired or Invalid</h2>
                        <p className="text-muted-foreground mb-6">{error}</p>
                        <Link to="/">
                            <Button className="rounded-full">
                                Go to MediGuide
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const config = urgencyConfig[diagnosis.urgency_level] || urgencyConfig.ROUTINE;
    const UrgencyIcon = config.icon;

    return (
        <div className="min-h-screen bg-background" data-testid="shared-diagnosis-page">
            {/* Header Banner */}
            <div className="bg-primary/5 border-b border-primary/10 py-4">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                                <Activity className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="font-heading font-bold text-lg">MediGuide</h1>
                                <p className="text-xs text-muted-foreground">Shared Health Assessment</p>
                            </div>
                        </div>
                        <Badge variant="outline" className="gap-2">
                            <Shield className="w-3 h-3" />
                            Verified Assessment
                        </Badge>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 py-8 lg:py-12">
                {/* Patient Info */}
                <Card className="mb-6 border-primary/20 bg-primary/5" data-testid="patient-info">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                                <User className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <p className="font-semibold text-lg">{diagnosis.patient_name}&apos;s Assessment</p>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Clock className="w-4 h-4" />
                                    {formatDate(diagnosis.created_at)}
                                    {diagnosis.has_image && (
                                        <Badge variant="outline" className="ml-2">
                                            <ImageIcon className="w-3 h-3 mr-1" />
                                            Image Included
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Urgency Banner */}
                <Card 
                    className={`mb-6 border-2 ${config.borderClass} ${config.bgClass}`}
                    data-testid="urgency-banner"
                >
                    <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                            <div className={`w-14 h-14 rounded-2xl ${config.class} flex items-center justify-center flex-shrink-0`}>
                                <UrgencyIcon className="w-7 h-7 text-white" />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                    <h2 className={`font-heading text-2xl font-bold ${config.textClass}`}>
                                        {config.label}
                                    </h2>
                                    <Badge className={config.class}>{diagnosis.urgency_level}</Badge>
                                </div>
                                <p className="text-lg">{diagnosis.urgency_description}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Symptoms Summary */}
                <Card className="mb-6" data-testid="symptoms-summary">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <Activity className="w-5 h-5 text-primary" />
                            Reported Symptoms
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-2">
                            {diagnosis.symptoms.symptoms.map((symptom, i) => (
                                <Badge key={i} variant="secondary" className="px-3 py-1">
                                    {symptom}
                                </Badge>
                            ))}
                        </div>
                        <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
                            <div>
                                <span className="text-muted-foreground">Body Parts:</span>
                                <p className="font-medium">{diagnosis.symptoms.body_parts.join(', ')}</p>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Duration:</span>
                                <p className="font-medium">{diagnosis.symptoms.duration}</p>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Severity:</span>
                                <p className="font-medium capitalize">{diagnosis.symptoms.severity.replace('_', ' ')}</p>
                            </div>
                        </div>
                        
                        {/* Medical History */}
                        {diagnosis.symptoms.medical_history && (
                            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                                <h4 className="text-sm font-medium mb-3">Medical History:</h4>
                                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                                    {diagnosis.symptoms.medical_history.conditions?.length > 0 && (
                                        <div>
                                            <span className="text-muted-foreground">Conditions:</span>
                                            <p className="font-medium">{diagnosis.symptoms.medical_history.conditions.join(', ')}</p>
                                        </div>
                                    )}
                                    {diagnosis.symptoms.medical_history.medications?.length > 0 && (
                                        <div>
                                            <span className="text-muted-foreground">Medications:</span>
                                            <p className="font-medium">{diagnosis.symptoms.medical_history.medications.join(', ')}</p>
                                        </div>
                                    )}
                                    {diagnosis.symptoms.medical_history.allergies?.length > 0 && (
                                        <div>
                                            <span className="text-muted-foreground">Allergies:</span>
                                            <p className="font-medium text-red-600">{diagnosis.symptoms.medical_history.allergies.join(', ')}</p>
                                        </div>
                                    )}
                                    {diagnosis.symptoms.medical_history.surgeries?.length > 0 && (
                                        <div>
                                            <span className="text-muted-foreground">Surgeries:</span>
                                            <p className="font-medium">{diagnosis.symptoms.medical_history.surgeries.join(', ')}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Doctor Notes Section */}
                <Card className="mb-6 border-primary/20" data-testid="doctor-notes-section">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <MessageSquare className="w-5 h-5 text-primary" />
                            Doctor&apos;s Notes
                        </CardTitle>
                        <CardDescription>
                            Add your professional notes and recommendations for the patient
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Existing Notes */}
                        {notes.length > 0 && (
                            <div className="space-y-3 mb-6">
                                {notes.map((note, i) => (
                                    <div 
                                        key={note.id || i} 
                                        className="p-4 rounded-xl bg-muted/50 border border-slate-200 dark:border-slate-700"
                                        data-testid={`doctor-note-${i}`}
                                    >
                                        <div className="flex items-center gap-2 mb-2">
                                            <UserCircle className="w-5 h-5 text-primary" />
                                            <span className="font-medium">{note.doctor_name}</span>
                                            <span className="text-xs text-muted-foreground">
                                                {new Date(note.added_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <p className="text-sm">{note.note}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Add Note Form */}
                        <form onSubmit={handleAddNote} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="doctorName">Your Name</Label>
                                <Input
                                    id="doctorName"
                                    placeholder="Dr. Smith"
                                    value={doctorName}
                                    onChange={(e) => setDoctorName(e.target.value)}
                                    data-testid="doctor-name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="note">Your Note</Label>
                                <Textarea
                                    id="note"
                                    placeholder="Enter your professional notes, recommendations, or follow-up instructions..."
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                    rows={4}
                                    data-testid="doctor-note-input"
                                />
                            </div>
                            <Button 
                                type="submit" 
                                className="rounded-full"
                                disabled={submittingNote}
                                data-testid="add-note-btn"
                            >
                                {submittingNote ? (
                                    <div className="spinner w-4 h-4 mr-2" />
                                ) : (
                                    <Send className="w-4 h-4 mr-2" />
                                )}
                                Add Note
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                {/* Tabbed Content */}
                <Tabs defaultValue="conditions" className="space-y-6">
                    <TabsList className="grid grid-cols-4">
                        <TabsTrigger value="conditions">Conditions</TabsTrigger>
                        <TabsTrigger value="recommendations">Advice</TabsTrigger>
                        <TabsTrigger value="treatment">Treatment</TabsTrigger>
                        <TabsTrigger value="investigations">Tests</TabsTrigger>
                    </TabsList>

                    <TabsContent value="conditions">
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading">AI-Suggested Conditions</CardTitle>
                                <CardDescription>Based on reported symptoms and medical history</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {diagnosis.possible_conditions.map((condition, i) => (
                                    <div key={i} className="p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <div className="flex items-start justify-between mb-2">
                                            <h4 className="font-semibold text-lg">{condition.name}</h4>
                                            <Badge variant={
                                                condition.likelihood === 'High' ? 'destructive' :
                                                condition.likelihood === 'Medium' ? 'default' : 'secondary'
                                            }>
                                                {condition.likelihood}
                                            </Badge>
                                        </div>
                                        <p className="text-muted-foreground">{condition.description}</p>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="recommendations">
                        <div className="grid gap-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="font-heading flex items-center gap-2">
                                        <Heart className="w-5 h-5 text-red-500" />
                                        First Aid Tips
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul className="space-y-3">
                                        {diagnosis.first_aid_tips.map((tip, i) => (
                                            <li key={i} className="flex items-start gap-3">
                                                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                                                <span>{tip}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle className="font-heading">Recommendations</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                                        <h4 className="font-semibold text-primary mb-2">Immediate Action</h4>
                                        <p>{diagnosis.recommendations.immediate_action}</p>
                                    </div>
                                    <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                                        <h4 className="font-semibold text-amber-700 dark:text-amber-400 mb-2">When to Seek Care</h4>
                                        <p>{diagnosis.recommendations.when_to_seek_care}</p>
                                    </div>
                                    <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                                        <h4 className="font-semibold text-emerald-700 dark:text-emerald-400 mb-2">Self-Care Tips</h4>
                                        <p>{diagnosis.recommendations.self_care}</p>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    <TabsContent value="treatment">
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <Pill className="w-5 h-5 text-primary" />
                                    Treatment Options
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ul className="space-y-3">
                                    {diagnosis.treatment_options.map((option, i) => (
                                        <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                                            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                <span className="text-xs font-bold text-primary">{i + 1}</span>
                                            </div>
                                            <span>{option}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-6 p-4 rounded-xl bg-muted">
                                    <h4 className="font-semibold mb-2">Expected Outcomes</h4>
                                    <p className="text-muted-foreground">{diagnosis.expected_outcomes}</p>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="investigations">
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <Search className="w-5 h-5 text-primary" />
                                    Recommended Tests
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {diagnosis.investigations.map((test, i) => (
                                        <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700">
                                            <FileText className="w-5 h-5 text-primary" />
                                            <span>{test}</span>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>

                {/* Disclaimer */}
                <Card className="mt-6 border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
                    <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <h4 className="font-semibold text-amber-800 dark:text-amber-300 mb-1">Important Disclaimer</h4>
                                <p className="text-sm text-amber-700 dark:text-amber-400">
                                    {diagnosis.disclaimer}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Footer */}
                <div className="mt-8 text-center text-sm text-muted-foreground">
                    <p>This assessment was generated by MediGuide</p>
                    <Link to="/" className="text-primary hover:underline">
                        Learn more about MediGuide
                    </Link>
                </div>
            </div>
        </div>
    );
}
